/* eslint-disable max-len */
import { appRoutePaths } from '../services/route.service';
import { DyslexiaScreenerContactModel } from '../types/DyslexiaScreenerContactModel';
import { DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_OPTIONS } from '../types/DyslexiaScreenerDocumentUploadType';
import { DyslexiaScreenerModel } from '../types/DyslexiaScreenerModel';
import { SelectOption } from '../types/SelectOption';
import { DyslexiaScreenerListPageValidationSchema } from '../types/Validation/DyslexiaScreenerListPageValidationSchema';

const DYSLEXIA_SCREENER_TRAINING_CYCLE_START_YEAR = 2024;
const DYSLEXIA_SCREENER_FIRST_ORDINAL_SCHOOL_YEAR_END = 2025;

/** Dropdown value for "Other" under Training Sources Used; drives conditional "Other Training Sources Used" validation. */
export const DYSLEXIA_SCREENER_TRAINING_SOURCE_OTHER_VALUE = 'Other';

export const DYSLEXIA_SCREENER_TRAINING_SOURCE_OPTIONS: SelectOption[] = [
  { value: 'MDE Training Videos', text: 'MDE Training Videos' },
  { value: DYSLEXIA_SCREENER_TRAINING_SOURCE_OTHER_VALUE, text: 'Other' },
];

export const DYSLEXIA_SCREENER_ANNUAL_ACKNOWLEDGEMENT_TEXT =
  'I acknowledge and certify compliance with the training requirements for the selected school year.';

export const DYSLEXIA_SCREENER_NEW_STAFF_TRAINING_YES = 'Yes';
export const DYSLEXIA_SCREENER_NEW_STAFF_TRAINING_NO = 'No';

export const isTrainingAcknowledgementDatePopulated = (
  date: unknown
): boolean => typeof date === 'string' && date.trim().length > 0;

export const returnDyslexiaScreenerTrainingCycle = (
  schoolYear: number
): number => {
  const year = Number(schoolYear);
  if (
    !Number.isFinite(year) ||
    year < DYSLEXIA_SCREENER_TRAINING_CYCLE_START_YEAR
  ) {
    return 0;
  }
  return (
    Math.floor(((year-1) - DYSLEXIA_SCREENER_TRAINING_CYCLE_START_YEAR) / 3) + 1
  );
};

export const returnDyslexiaScreenerTrainingYearOrdinalInBlock = (
  schoolYear: number
): number => {
  const year = Number(schoolYear);
  if (
    !Number.isFinite(year) ||
    year < DYSLEXIA_SCREENER_TRAINING_CYCLE_START_YEAR
  ) {
    return 0;
  }
  if (year < DYSLEXIA_SCREENER_FIRST_ORDINAL_SCHOOL_YEAR_END) {
    return 1;
  }
  return ((year - DYSLEXIA_SCREENER_FIRST_ORDINAL_SCHOOL_YEAR_END) % 3) + 1;
};

/** UI-only new-staff Yes/No for 2nd & 3rd school years in each MDE window; updated by the form. */
export const dyslexiaScreenerNewStaffTrainingUiRef = {
  newStaffTrainingRequired: '',
};

export const dyslexiaScreenerPendingFileUploadRef = {
  hasPendingFile: false,
};

/** True when optional "Training Sources" / "Other" fields have data (drives new-staff Yes/No derivation). */
export const hasDyslexiaScreenerTrainingDetailValues = (
  model: Pick<DyslexiaScreenerModel, 'trainingUsed' | 'otherTrainingUsed'>
): boolean =>
  Boolean(model.trainingUsed?.trim()) ||
  Boolean(model.otherTrainingUsed?.trim());

export const shouldShowDyslexiaScreenerNewStaffTrainingRadio = (
  schoolYear: number,
  trainingAcknowledgementDate: string
): boolean => {
  if (!isTrainingAcknowledgementDatePopulated(trainingAcknowledgementDate)) {
    return false;
  }
  const ordinal = returnDyslexiaScreenerTrainingYearOrdinalInBlock(schoolYear);
  return ordinal >= 2;
};

export const shouldShowDyslexiaScreenerTrainingDetailFields = (
  schoolYear: number,
  trainingAcknowledgementDate: string,
  newStaffTrainingRequired: string
): boolean => {
  if (!isTrainingAcknowledgementDatePopulated(trainingAcknowledgementDate)) {
    return false;
  }
  const ordinal = returnDyslexiaScreenerTrainingYearOrdinalInBlock(schoolYear);
  if (ordinal === 1) {
    return true;
  }
  if (ordinal >= 2) {
    return (
      newStaffTrainingRequired === DYSLEXIA_SCREENER_NEW_STAFF_TRAINING_YES
    );
  }
  return false;
};

/** Ordinal 2–3 in MDE window: Yes when any training detail is saved; otherwise No. */
export const deriveDyslexiaScreenerNewStaffTrainingRequired = (
  schoolYear: number,
  trainingAcknowledgementDate: string,
  model: Pick<DyslexiaScreenerModel, 'trainingUsed' | 'otherTrainingUsed'>
): string => {
  if (!isTrainingAcknowledgementDatePopulated(trainingAcknowledgementDate)) {
    return '';
  }
  const ordinal = returnDyslexiaScreenerTrainingYearOrdinalInBlock(schoolYear);
  if (ordinal >= 2) {
    return hasDyslexiaScreenerTrainingDetailValues(model)
      ? DYSLEXIA_SCREENER_NEW_STAFF_TRAINING_YES
      : DYSLEXIA_SCREENER_NEW_STAFF_TRAINING_NO;
  }
  return '';
};

export const requiresDyslexiaScreenerTrainingDetails = (parent: {
  schoolYear: number;
  trainingAcknowledgementDate: string;
}): boolean =>
  shouldShowDyslexiaScreenerTrainingDetailFields(
    parent.schoolYear,
    parent.trainingAcknowledgementDate ?? '',
    dyslexiaScreenerNewStaffTrainingUiRef.newStaffTrainingRequired
  );

/** Document type value for training attendance / participants list upload. */
export const DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_TRAINING_ATTENDANCE_TYPE =
  DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_OPTIONS[0].value;

/** Document type value for written permission when training source is Other. */
export const DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_OTHER_PERMISSION_TYPE =
  DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_OPTIONS[1].value;

export interface DyslexiaScreenerDocumentUploadRuleInput {
  schoolYear: number;
  trainingAcknowledgementDate: string;
  trainingUsed: string;
}

/**
 * Which dyslexia training document uploads are required for the current form state
 * (same rules as the Upload Documents panel). Uses {@link dyslexiaScreenerNewStaffTrainingUiRef}
 * for new-staff Yes/No so validation matches the training panel before Redux syncs.
 */
export const dyslexiaScreenerDocumentUploadRequirementFlags = (
  input: DyslexiaScreenerDocumentUploadRuleInput
): {
  requiresTrainingParticipantsDocument: boolean;
  requiresOtherTrainingPermissionDocument: boolean;
} => {
  const schoolYearNum = Number(input.schoolYear);
  const ordinal =
    returnDyslexiaScreenerTrainingYearOrdinalInBlock(schoolYearNum);
  const ackPopulated = isTrainingAcknowledgementDatePopulated(
    input.trainingAcknowledgementDate ?? ''
  );
  const trainingUsedTrimmed = (input.trainingUsed ?? '').trim();
  const trainingSourceIsOther =
    trainingUsedTrimmed === DYSLEXIA_SCREENER_TRAINING_SOURCE_OTHER_VALUE;
  const newStaffYes =
    dyslexiaScreenerNewStaffTrainingUiRef.newStaffTrainingRequired ===
    DYSLEXIA_SCREENER_NEW_STAFF_TRAINING_YES;

  const requiresTrainingParticipantsDocument =
    !trainingSourceIsOther &&
    ordinal > 0 &&
    ((ordinal === 1 && ackPopulated) || (ordinal >= 2 && newStaffYes));

  const requiresOtherTrainingPermissionDocument = trainingSourceIsOther;

  return {
    requiresTrainingParticipantsDocument,
    requiresOtherTrainingPermissionDocument,
  };
};

export interface DyslexiaScreenerDocumentUploadMissingFlags {
  missingTrainingParticipants: boolean;
  missingOtherPermission: boolean;
}

export const dyslexiaScreenerUploadedFilesSatisfyDocumentRequirements = (
  uploadedFiles: readonly { documentType?: string | null }[],
  flags: ReturnType<typeof dyslexiaScreenerDocumentUploadRequirementFlags>
): DyslexiaScreenerDocumentUploadMissingFlags => {
  const hasTraining = uploadedFiles.some(
    (f) =>
      (f.documentType ?? '').trim() ===
      DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_TRAINING_ATTENDANCE_TYPE
  );
  const hasOther = uploadedFiles.some(
    (f) =>
      (f.documentType ?? '').trim() ===
      DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_OTHER_PERMISSION_TYPE
  );

  return {
    missingTrainingParticipants:
      flags.requiresTrainingParticipantsDocument && !hasTraining,
    missingOtherPermission:
      flags.requiresOtherTrainingPermissionDocument && !hasOther,
  };
};

export const isDyslexiaScreenerNewStaffTrainingSelectionRequired = (parent: {
  schoolYear: number;
  trainingAcknowledgementDate: string;
}): boolean =>
  shouldShowDyslexiaScreenerNewStaffTrainingRadio(
    parent.schoolYear,
    parent.trainingAcknowledgementDate ?? ''
  );

const formatTrainingCycleBoundaryDate = (date: Date): string =>
  date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

/** Training cycle year range from school year: cycle*3+2024 through +3. */
export const formatDyslexiaScreenerTrainingCycleDisplay = (
  schoolYear: number
): string => {
  const year = Number(schoolYear);
  if (!Number.isFinite(year) || year <= 0) return '';
  const trainingCycle = returnDyslexiaScreenerTrainingCycle(year);
  if (trainingCycle <= 0) return '';
  const cycleIndex = trainingCycle - 1;
  const startYear =
    cycleIndex * 3 + DYSLEXIA_SCREENER_TRAINING_CYCLE_START_YEAR;
  const endYear =
    cycleIndex * 3 + DYSLEXIA_SCREENER_TRAINING_CYCLE_START_YEAR + 3;
  const cycleStart = new Date(startYear, 6, 1);
  const cycleEnd = new Date(endYear, 5, 30);
  return `${formatTrainingCycleBoundaryDate(
    cycleStart
  )} - ${formatTrainingCycleBoundaryDate(cycleEnd)}`;
};

/** Display string for failed/at-risk rate; not persisted by the API. */
export const formatDyslexiaScreenerFailedOrAtRiskPercentage = (
  totalRaw: unknown,
  failedRaw: unknown
): string => {
  const total = Number(totalRaw);
  const failed = Number(failedRaw);
  // if (!Number.isFinite(total) || !Number.isFinite(failed)) return 'N/A';
  if (total <= 0) {
    return failed <= 0 ? '0%' : '';
  }
  return `${Math.round((failed / total) * 100)}%`;
};

/**
 * True when LEA dyslexia contact is persisted (server id) and all list-page required
 * fields pass the same validation as the contact form. Used to gate list filters and
 * row navigation until the first successful save.
 */
/** List-page filter values persisted in the URL query string. */
export interface DyslexiaScreenerListFilters {
  lea: string;
  school: string;
  type: string;
  status: string;
}

export const parseDyslexiaScreenerListFiltersFromSearch = (
  search: string
): DyslexiaScreenerListFilters => {
  const params = new URLSearchParams(search);
  return {
    lea: params.get('lea') || '',
    school: params.get('school') || '',
    type: params.get('type') || '',
    status: params.get('status') || '',
  };
};

export const buildDyslexiaScreenerListFilterQueryString = (
  filters: Partial<DyslexiaScreenerListFilters>
): string => {
  const params = new URLSearchParams();
  if (filters.lea) params.set('lea', filters.lea);
  if (filters.school) params.set('school', filters.school);
  if (filters.type) params.set('type', filters.type);
  if (filters.status) params.set('status', filters.status);
  return params.toString();
};

export const buildDyslexiaScreenerListRoute = (
  schoolYear: string,
  filters?: Partial<DyslexiaScreenerListFilters>
): string => {
  const queryString = filters
    ? buildDyslexiaScreenerListFilterQueryString(filters)
    : '';
  const base = `${appRoutePaths.DyslexiaScreenerList(schoolYear)}`;
  return queryString ? `${base}?${queryString}` : base;
};

export const isPersistedLeaDyslexiaContactInformationComplete = (
  contact: DyslexiaScreenerContactModel | null | undefined
): boolean => {
  if (!contact?.id?.trim()) {
    return false;
  }
  try {
    DyslexiaScreenerListPageValidationSchema.validateSync(
      {
        firstName: contact.firstName,
        lastOrSurname: contact.lastOrSurname,
        email: contact.email,
        telephone: contact.telephone,
      },
      { abortEarly: false }
    );
    return true;
  } catch {
    return false;
  }
};
